import {
  ApiNewDokumentPowiazanyDalDto,
  EgibObject,
  MapState,
} from '@gk/gk-modules';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { WorkspaceState } from '../../../../services/request-workspace-state/request-workspace-state.model';
import {
  DocumentationFormType,
  ExtendedApplicantRequest,
  ExtendedApplicantRequestDto,
} from '../../../../shared/base-new-request/base-new-request.model';
import { InvestorDetails } from '../../../../shared/investor-details/investor-details.model';
import { Shipping } from '../../../../shared/shipping-form/shipping-form.model';
import {
  ApiTimeString,
  DateHelperUtil,
  NgbDateRange,
} from '../../../../utils/date-helper/date-helper.util';
import {
  FinancialSectorApplication,
  FinancialSectorApplicationDto,
} from '../../financial-sector-application-form-array/financial-sector-application-form-array.model';
import {
  OtherConservationActivity,
  OtherConservationActivityDto,
} from '../../other-conservation-activities-form-array/other-conservation-activities-form-array.model';
import {
  EstimatedWorksCosts,
  EstimatedWorksCostsDto,
} from '../../works-estimated-costs/works-estimated-costs-form.model';
import {
  MCGRLandAndMortgageRegisterNumberAndDocumentsCollectionNumberFormControlName,
  MCGRPermitFormControlName,
} from '../new-request-form/new-request-form.model';

export enum MCGRConstructionPermitDocumentType {
  BuildingPermit = 'buildingPermit',
  ConstructionWorkNotifiction = 'constructionWorkNotification',
}

export interface MCGRNewRequestDto extends ExtendedApplicantRequestDto {
  NazwaZadania: string;
  NazwaZabytku: string;
  NrWRejestrzeZabytkow: string;
  DataWpisaniaWRejestrzeZabytkow: string;
  Lokalizacja?: string;
  UzasadnienieIstotnegoZnaczenia: string;
  OsobaDoKontaktu: string;
  NrKontaWnioskodawcy: string;
  TytulPrawnyDoZabytku: string;
  NrKw: string;
  NrZbioruDokumentow: string;
  PozwolenieNaPraceKonserwatorskieWydanePrzez: string;
  PozwolenieNaPraceKonserwatorskieZDnia: string;
  PozwolenieNaPraceKonserwatorskieNrPozwolenia: string;
  PozwolenieNaBudoweWydanePrzez?: string;
  PozwolenieNaBudoweZDnia?: string;
  PozwolenieNaBudoweNrPozwolenia?: string;
  ZgloszeniePracBudowlanychZDnia?: string;
  ZakresPrac: string;
  UzasadnienieCelowosciPrac: string;
  TerminRozpoczeciaPrac: string;
  TerminZakonczeniaPrac: string;
  WniosekDoInnychJednostekSektoraFinansowego: FinancialSectorApplicationDto[];
  PrzewidywaneKosztyPrac: EstimatedWorksCostsDto;
  OpiekaNadZabytkiem: string;
  CzyJestKsiazkaObiektuBudowlanego: boolean;
  CzyWykonywaneSaCorocznePrzegladyTechniczne: boolean;
  CzyWykonywaneSa5LetniePrzegladyTechniczne: boolean;
  InneDzialania: string;
  InneDzialaniaKonserwatorskie: OtherConservationActivityDto[];
  OswiadczenieOWydatkowaniuSrodkow: boolean;
  OswiadczenieOZapoznaniuSieZUchwala: boolean;
}

export interface MCGRLandAndMortgageRegisterNumberAndDocumentsCollectionNumber {
  [MCGRLandAndMortgageRegisterNumberAndDocumentsCollectionNumberFormControlName.LandAndMortgageRegisterNumber]: string;
  [MCGRLandAndMortgageRegisterNumberAndDocumentsCollectionNumberFormControlName.DocumentsCollectionNumber]: string;
}

export interface MCGRConservationPermit {
  [MCGRPermitFormControlName.IssuedBy]: string;
  [MCGRPermitFormControlName.Date]: NgbDate;
  [MCGRPermitFormControlName.Number]: string;
}

export interface MCGRConstructionPermit extends MCGRConservationPermit {
  [MCGRPermitFormControlName.Type]: MCGRConstructionPermitDocumentType;
}

export class MCGRNewRequest extends ExtendedApplicantRequest {
  constructor(
    investorDetails: InvestorDetails,
    documentationForm: DocumentationFormType,
    shippingForm: Shipping,
    proxyDetailsCheckbox: boolean,
    proxyDetailsArray: InvestorDetails[],
    public taskName: string,
    public monumentName: string,
    public monumentRegistryNumber: string,
    public monumentRegistryDate: NgbDate,
    public determinationOfLocation: string | undefined,
    public justificationOfImportance: string,
    public contactPerson: string,
    public applicantAccountNumber: string,
    public legalTitleToMonument: string,
    public landAndMortgageRegisterNumberAndDocumentsCollectionNumber: MCGRLandAndMortgageRegisterNumberAndDocumentsCollectionNumber,
    public conservationPermit: MCGRConservationPermit,
    public constructionPermit: MCGRConstructionPermit,
    public scopeOfWorks: string,
    public justificationOfWorksPurpose: string,
    public worksDateRange: NgbDateRange,
    public financialSectorApplication: FinancialSectorApplication[],
    public worksEstimatedCosts: EstimatedWorksCosts,
    public monumentCare: string,
    public hasBuildingBook: boolean,
    public hasAnnualTechInspections: boolean,
    public hasFiveYearTechInspections: boolean,
    public otherActivities: string,
    public otherConservationActivities: OtherConservationActivity[],
    public expenditureDeclaration: boolean,
    public awarenessAndTruthfulnessDeclaration: boolean,
  ) {
    super(
      investorDetails,
      documentationForm,
      shippingForm,
      undefined,
      undefined,
      proxyDetailsCheckbox,
      proxyDetailsArray,
    );
  }

  static fromAppToApi(
    data: MCGRNewRequest,
    parcels: EgibObject[],
    buildings: EgibObject[],
    files: ApiNewDokumentPowiazanyDalDto[],
  ): MCGRNewRequestDto {
    return {
      ...ExtendedApplicantRequest.mapCommonFields({
        data,
        files,
        parcels,
        buildings,
      }),
      NazwaZadania: data.taskName,
      NazwaZabytku: data.monumentName,
      NrWRejestrzeZabytkow: data.monumentRegistryNumber,
      DataWpisaniaWRejestrzeZabytkow: DateHelperUtil.ngbDateToApiDateTime(
        data.monumentRegistryDate,
      ),
      Lokalizacja: data.determinationOfLocation,
      UzasadnienieIstotnegoZnaczenia: data.justificationOfImportance,
      OsobaDoKontaktu: data.contactPerson,
      NrKontaWnioskodawcy: data.applicantAccountNumber,
      TytulPrawnyDoZabytku: data.legalTitleToMonument,
      NrKw: data.landAndMortgageRegisterNumberAndDocumentsCollectionNumber
        ?.landAndMortgageRegisterNumber,
      NrZbioruDokumentow:
        data.landAndMortgageRegisterNumberAndDocumentsCollectionNumber
          ?.documentsCollectionNumber,
      PozwolenieNaPraceKonserwatorskieWydanePrzez:
        data.conservationPermit?.issuedBy,
      PozwolenieNaPraceKonserwatorskieZDnia: data.conservationPermit?.date
        ? DateHelperUtil.ngbDateToApiDateTime(data.conservationPermit.date)
        : undefined,
      PozwolenieNaPraceKonserwatorskieNrPozwolenia:
        data.conservationPermit?.number,
      ZakresPrac: data.scopeOfWorks,
      UzasadnienieCelowosciPrac: data.justificationOfWorksPurpose,
      TerminRozpoczeciaPrac: DateHelperUtil.ngbDateToApiDateTime(
        data?.worksDateRange?.from,
      ),
      TerminZakonczeniaPrac: DateHelperUtil.ngbDateToApiDateTime(
        data?.worksDateRange?.to,
        ApiTimeString.End,
      ),
      WniosekDoInnychJednostekSektoraFinansowego:
        data.financialSectorApplication?.map((item) =>
          FinancialSectorApplicationDto.fromAppToApi(item),
        ),
      PrzewidywaneKosztyPrac: data.worksEstimatedCosts
        ? EstimatedWorksCostsDto.fromAppToApi(data.worksEstimatedCosts)
        : undefined,
      OpiekaNadZabytkiem: data.monumentCare,
      CzyJestKsiazkaObiektuBudowlanego: data.hasBuildingBook,
      CzyWykonywaneSaCorocznePrzegladyTechniczne: data.hasAnnualTechInspections,
      CzyWykonywaneSa5LetniePrzegladyTechniczne:
        data.hasFiveYearTechInspections,
      InneDzialania: data.otherActivities,
      InneDzialaniaKonserwatorskie: data.otherConservationActivities?.map(
        (item) => OtherConservationActivityDto.fromAppToApi(item),
      ),
      OswiadczenieOWydatkowaniuSrodkow: data.expenditureDeclaration,
      ...this.getConstructionPermitData(data),
      OswiadczenieOZapoznaniuSieZUchwala:
        data.awarenessAndTruthfulnessDeclaration,
    };
  }

  static getConstructionPermitData(form: MCGRNewRequest):
    | {
        PozwolenieNaBudoweWydanePrzez: string;
        PozwolenieNaBudoweZDnia: string;
        PozwolenieNaBudoweNrPozwolenia: string;
      }
    | {
        ZgloszeniePracBudowlanychZDnia: string;
      } {
    if (
      form.constructionPermit?.type ===
      MCGRConstructionPermitDocumentType.BuildingPermit
    ) {
      return {
        PozwolenieNaBudoweWydanePrzez: form.constructionPermit?.issuedBy,
        PozwolenieNaBudoweZDnia: form.constructionPermit.date
          ? DateHelperUtil.ngbDateToApiDateTime(form.constructionPermit.date)
          : undefined,
        PozwolenieNaBudoweNrPozwolenia: form.constructionPermit?.number,
      };
    } else {
      return {
        ZgloszeniePracBudowlanychZDnia: DateHelperUtil.ngbDateToApiDateTime(
          form.constructionPermit.date,
        ),
      };
    }
  }
}

export interface MCGRNewRequestFormWorkspaceState extends WorkspaceState {
  formValue: MCGRNewRequest;
  mapState: MapState;
}
